import { useState } from 'react';
import { Badge, Card, Button, Select, LoadingSkeleton } from '@nexora/design-system';
import { formatCurrency } from '../../shared/utils/format';
import {
  useRevenueRecognition,
  useAccountingCtx,
  type RevenueAnomalyRow,
  type RevenueContractRow,
  type RevenueScheduleRow,
} from './useAccounting';

/* ═══════════════════════════════════════════════════════════════════════════
   Reconhecimento de Receita / Diferimento — IFRS 15 / CPC 47
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido:

   GET /accounting/revenue-recognition?monthFrom&monthTo&limit
   → RevenueRecognitionDashboardDto {
       summary: { totalContracts, totalForecast, totalActual, totalVariance,
                  complianceByStatus: { IFRS15_ALIGNED, REVIEW_NEEDED, NON_COMPLIANT } },
       schedule: RecognitionMonthRowDto[] { month, forecast, actual, variance, contracts },
       contracts: RecognitionContractRowDto[] { id, contractId, clientName,
                  recognizedAmount, complianceStatus, totalForecast, totalActual,
                  variance, months[] },
       anomalies: RecognitionAnomalyRowDto[] { contractId, clientName, month,
                  type, severity, message },
       generatedAt
     }

   ATENÇÃO (divergência com o contexto da task): a query GetRevenueRecognitionQuery
   e os DTOs existem no backend, mas o CONTROLLER HTTP GET /accounting/revenue-recognition
   ainda não foi registrado no AccountingModule (status em out/2026). A página é
   construída contra o contrato do DTO; enquanto a rota não existir, o erro 404
   aparece como estado vazio explícito com detalhe. Regra de ouro do módulo: o
   backend NÃO estima valores — "actual" é soma do reconhecido nos contratos.
   ═══════════════════════════════════════════════════════════════════════════ */

const COMPLIANCE_META: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'neutral' }> = {
  IFRS15_ALIGNED: { label: 'IFRS 15 Alinhado', variant: 'success' },
  REVIEW_NEEDED: { label: 'Revisão Necessária', variant: 'warning' },
  NON_COMPLIANT: { label: 'Não Conforme', variant: 'error' },
};

function complianceBadge(status: string): JSX.Element {
  const meta = COMPLIANCE_META[status] ?? { label: status, variant: 'neutral' as const };
  return (
    <Badge variant={meta.variant} size="sm">
      {meta.label}
    </Badge>
  );
}

const SEVERITY_META: Record<string, { label: string; variant: 'error' | 'warning' | 'info' | 'neutral' }> = {
  HIGH: { label: 'Alta', variant: 'error' },
  MEDIUM: { label: 'Média', variant: 'warning' },
  LOW: { label: 'Baixa', variant: 'info' },
};

function severityBadge(severity: string): JSX.Element {
  const meta = SEVERITY_META[severity] ?? { label: severity, variant: 'neutral' as const };
  return (
    <Badge variant={meta.variant} size="sm">
      {meta.label}
    </Badge>
  );
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-');
  if (!y || !m) return month;
  const names = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const idx = Number(m) - 1;
  return `${names[idx] ?? m}/${y}`;
}

const RrStateLoading = ({ label }: { label: string }): JSX.Element => (
  <div className="rr-state">
    <LoadingSkeleton />
    <span>{label}</span>
  </div>
);

const RrStateError = ({ label, error }: { label: string; error: unknown }): JSX.Element => (
  <div className="rr-state rr-state--error">
    <p className="rr-state__title">{label}</p>
    <p className="rr-state__detail">{error instanceof Error ? error.message : 'Erro desconhecido'}</p>
    {error instanceof Error && /404|not found/i.test(error.message) && (
      <p className="rr-state__hint">
        A rota GET /accounting/revenue-recognition ainda não está registrada no backend
        (a query e o DTO já existem). Assim que o controller for exposto, esta tela passa a
        exibir os dados sem nenhuma alteração de código.
      </p>
    )}
  </div>
);

const RrStateEmpty = ({ label }: { label: string }): JSX.Element => (
  <div className="rr-state rr-state--empty">
    <p>{label}</p>
  </div>
);

/* ── Gráfico Forecast vs Actuals (divs, sem lib) ── */

function ForecastActualChart({ schedule }: { schedule: RevenueScheduleRow[] }): JSX.Element {
  if (schedule.length === 0) return <p className="rr-muted-center">Sem cronograma mensal para exibir.</p>;

  const maxAbs = Math.max(1, ...schedule.map((s) => Math.max(Math.abs(s.forecast), Math.abs(s.actual))));

  return (
    <div className="rr-chart">
      <div className="rr-chart__legend">
        <span className="rr-chart__key"><span className="rr-swatch rr-swatch--forecast" /> Forecast</span>
        <span className="rr-chart__key"><span className="rr-swatch rr-swatch--actual" /> Actual</span>
      </div>
      <div className="rr-chart__rows">
        {schedule.map((row) => (
          <div key={row.month} className="rr-chart__row">
            <span className="rr-chart__month">{monthLabel(row.month)}</span>
            <div className="rr-chart__bars">
              <div className="rr-chart__bar rr-chart__bar--forecast" style={{ width: `${(row.forecast / maxAbs) * 100}%` }} />
              <div className="rr-chart__bar rr-chart__bar--actual" style={{ width: `${(row.actual / maxAbs) * 100}%` }} />
            </div>
            <span className="rr-chart__val">{formatCurrency(row.actual)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Cards ── */

function KpiCard({ title, value, tone }: { title: string; value: string; tone?: 'pos' | 'neg' | 'neutral' }): JSX.Element {
  return (
    <div className="rr-kpi">
      <span className="rr-kpi__title">{title}</span>
      <span className={`rr-kpi__value rr-kpi__value--${tone ?? 'neutral'}`}>{value}</span>
    </div>
  );
}

/* ── Anomalias ── */

function AnomalyRow({ anomaly }: { anomaly: RevenueAnomalyRow }): JSX.Element {
  return (
    <li className="rr-anom">
      <div className="rr-anom__top">
        <span className="rr-anom__contract">{anomaly.contractId}</span>
        <span className="rr-anom__meta">
          {anomaly.clientName ?? '—'} · {monthLabel(anomaly.month)}
        </span>
        {severityBadge(anomaly.severity)}
      </div>
      <p className="rr-anom__message">{anomaly.message}</p>
      <span className="rr-anom__type">{anomaly.type}</span>
    </li>
  );
}

/* ── Schedule por contrato ── */

function ContractRow({ contract }: { contract: RevenueContractRow }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="rr-contract" onClick={() => setOpen(!open)}>
        <td className="rr-mono">{contract.contractId}</td>
        <td>{contract.clientName ?? '—'}</td>
        <td>{complianceBadge(contract.complianceStatus)}</td>
        <td className="rr-num">{formatCurrency(contract.totalForecast)}</td>
        <td className="rr-num">{formatCurrency(contract.totalActual)}</td>
        <td className="rr-num">{formatCurrency(contract.variance)}</td>
        <td className="rr-center">
          <span className="material-symbols-outlined rr-chev" aria-hidden="true">
            {open ? 'expand_less' : 'expand_more'}
          </span>
        </td>
      </tr>
      {open && contract.months.length > 0 && (
        <tr className="rr-contract-detail">
          <td colSpan={7}>
            <table className="rr-table rr-table--nested">
              <thead>
                <tr>
                  <th>Mês</th>
                  <th className="rr-right">Forecast</th>
                  <th className="rr-right">Actual</th>
                  <th className="rr-right">Variação</th>
                </tr>
              </thead>
              <tbody>
                {contract.months.map((m) => (
                  <tr key={m.month}>
                    <td>{monthLabel(m.month)}</td>
                    <td className="rr-num">{formatCurrency(m.forecast)}</td>
                    <td className="rr-num">{formatCurrency(m.actual)}</td>
                    <td className="rr-num">{formatCurrency(m.variance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Page ── */

export function RevenueRecognitionPage(): JSX.Element {
  const today = new Date();
  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const monthsAgo = (n: number): string => {
    const d = new Date(today.getFullYear(), today.getMonth() - n, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const [period, setPeriod] = useState<'all' | 'ytd' | 'last12'>('all');

  const monthFrom = period === 'all' ? undefined : period === 'ytd' ? `${today.getFullYear()}-01` : monthsAgo(11);
  const monthTo = currentMonth;

  const ctx = useAccountingCtx();
  const { data, isLoading, error, refetch } = useRevenueRecognition(ctx, {
    limit: 100,
    monthFrom,
    monthTo: period === 'all' ? undefined : monthTo,
  });

  const summary = data?.summary;
  const complianceByStatus = summary?.complianceByStatus ?? {};

  const [complianceTotal, aligned, review, nonCompliant] = [
    Object.values(complianceByStatus).reduce((s, n) => s + n, 0),
    complianceByStatus['IFRS15_ALIGNED'] ?? 0,
    complianceByStatus['REVIEW_NEEDED'] ?? 0,
    complianceByStatus['NON_COMPLIANT'] ?? 0,
  ];

  return (
    <div className="rr-page">
      <div className="mb-lg">
        <h1 className="font-display-lg text-display-lg text-on-surface m-0">
          Reconhecimento de Receita
        </h1>
        <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
          Diferimento e reconhecimento conforme IFRS 15 / CPC 47 — forecast vs actuals.
        </p>
      </div>

      <div className="rr-toolbar">
        <Select
          label="Período"
          selectSize="sm"
          value={period}
          onChange={(e) => setPeriod(e.target.value as 'all' | 'ytd' | 'last12')}
          aria-label="Período"
        >
          <option value="all">Todos os meses</option>
          <option value="ytd">Ano atual (YTD)</option>
          <option value="last12">Últimos 12 meses</option>
        </Select>
        <Button variant="secondary" size="sm" onClick={() => refetch()}>Atualizar</Button>
      </div>

      {isLoading && <RrStateLoading label="Carregando reconhecimento de receita..." />}
      {error && !isLoading && (
        <RrStateError label="Erro ao carregar reconhecimento de receita" error={error} />
      )}

      {!isLoading && !error && data && (
        <>
          <div className="rr-kpis">
            <KpiCard title="Contratos" value={String(summary?.totalContracts ?? 0)} tone="neutral" />
            <KpiCard title="Total Forecast" value={formatCurrency(summary?.totalForecast ?? 0)} tone="neutral" />
            <KpiCard
              title="Total Reconhecido"
              value={formatCurrency(summary?.totalActual ?? 0)}
              tone={(summary?.totalVariance ?? 0) < 0 ? 'neg' : 'pos'}
            />
            <KpiCard
              title="Variação (actual - forecast)"
              value={formatCurrency(summary?.totalVariance ?? 0)}
              tone={(summary?.totalVariance ?? 0) < 0 ? 'neg' : 'pos'}
            />
          </div>

          <div className="rr-grid">
            <Card variant="outlined" padding="1.25rem">
              <div className="rr-card__head">
                <h3 className="rr-card__title">Recognition: Forecast vs Actuals</h3>
                <span className="rr-card__meta">mensal</span>
              </div>
              {(data.schedule ?? []).length === 0 ? (
                <RrStateEmpty label="Sem cronograma mensal de reconhecimento." />
              ) : (
                <ForecastActualChart schedule={data.schedule ?? []} />
              )}
            </Card>

            <Card variant="outlined" padding="1.25rem">
              <div className="rr-card__head">
                <h3 className="rr-card__title">Compliance Status</h3>
                <span className="rr-card__meta">IFRS 15 / CPC 47</span>
              </div>
              {complianceTotal === 0 ? (
                <RrStateEmpty label="Nenhum contrato com status de compliance." />
              ) : (
                <div className="rr-compliance">
                  <div className="rr-compliance__row">
                    <span className="rr-compliance__label">IFRS 15 Alinhado</span>
                    <div className="rr-compliance__track">
                      <div
                        className="rr-compliance__fill rr-compliance__fill--aligned"
                        style={{ width: `${(aligned / complianceTotal) * 100}%` }}
                      />
                    </div>
                    <span className="rr-compliance__num">{aligned}</span>
                  </div>
                  <div className="rr-compliance__row">
                    <span className="rr-compliance__label">Revisão Necessária</span>
                    <div className="rr-compliance__track">
                      <div
                        className="rr-compliance__fill rr-compliance__fill--review"
                        style={{ width: `${(review / complianceTotal) * 100}%` }}
                      />
                    </div>
                    <span className="rr-compliance__num">{review}</span>
                  </div>
                  <div className="rr-compliance__row">
                    <span className="rr-compliance__label">Não Conforme</span>
                    <div className="rr-compliance__track">
                      <div
                        className="rr-compliance__fill rr-compliance__fill--noncompliant"
                        style={{ width: `${(nonCompliant / complianceTotal) * 100}%` }}
                      />
                    </div>
                    <span className="rr-compliance__num">{nonCompliant}</span>
                  </div>
                </div>
              )}
            </Card>
          </div>

          <div className="rr-grid rr-grid--2col">
            <Card variant="outlined" padding="1.25rem">
              <div className="rr-card__head">
                <h3 className="rr-card__title">Recent Anomalies</h3>
                <span className="rr-card__meta">
                  {(data.anomalies ?? []).length} {(data.anomalies ?? []).length === 1 ? 'desvio' : 'desvios'}
                </span>
              </div>
              {(data.anomalies ?? []).length === 0 ? (
                <RrStateEmpty label="Nenhuma anomalia registrada no período." />
              ) : (
                <ul className="rr-anoms">
                  {(data.anomalies ?? []).slice(0, 10).map((a, i) => (
                    <AnomalyRow key={`${a.contractId}-${a.month}-${i}`} anomaly={a} />
                  ))}
                </ul>
              )}
            </Card>

            <Card variant="outlined" padding="0" className="rr-schedule-card">
              <div className="rr-card__head rr-card__head--pad">
                <h3 className="rr-card__title">Monthly Schedule</h3>
                <span className="rr-card__meta">
                  {(data.schedule ?? []).length} {(data.schedule ?? []).length === 1 ? 'mês' : 'meses'}
                </span>
              </div>
              {(data.schedule ?? []).length === 0 ? (
                <RrStateEmpty label="Sem cronograma mensal." />
              ) : (
                <div className="rr-scroll">
                  <table className="rr-table">
                    <thead>
                      <tr>
                        <th>Mês</th>
                        <th className="rr-right">Forecast</th>
                        <th className="rr-right">Actual</th>
                        <th className="rr-right">Variação</th>
                        <th className="rr-right">Contratos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.schedule ?? []).map((row) => (
                        <tr key={row.month} className="rr-schedule-row">
                          <td>{monthLabel(row.month)}</td>
                          <td className="rr-num">{formatCurrency(row.forecast)}</td>
                          <td className="rr-num">{formatCurrency(row.actual)}</td>
                          <td className={`rr-num ${row.variance < 0 ? 'rr-neg' : ''}`}>
                            {formatCurrency(row.variance)}
                          </td>
                          <td className="rr-num">{row.contracts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          <Card variant="outlined" padding="0" className="rr-contracts-card">
            <div className="rr-card__head rr-card__head--pad">
              <h3 className="rr-card__title">Contratos</h3>
              <span className="rr-card__meta">
                {(data.contracts ?? []).length} {(data.contracts ?? []).length === 1 ? 'contrato' : 'contratos'}
              </span>
            </div>
            {(data.contracts ?? []).length === 0 ? (
              <RrStateEmpty label="Nenhum contrato com cronograma de reconhecimento." />
            ) : (
              <div className="rr-scroll">
                <table className="rr-table">
                  <thead>
                    <tr>
                      <th>Contract ID</th>
                      <th>Client</th>
                      <th>Compliance</th>
                      <th className="rr-right">Forecast</th>
                      <th className="rr-right">Recognized</th>
                      <th className="rr-right">Variação</th>
                      <th className="rr-center" />
                    </tr>
                  </thead>
                  <tbody>
                    {(data.contracts ?? []).map((c) => (
                      <ContractRow key={c.id} contract={c} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      <style>{`
        .rr-page { padding: 1.5rem; }

        .rr-toolbar { display: flex; align-items: flex-end; gap: 0.75rem; margin-bottom: 1rem; flex-wrap: wrap; }
        .rr-toolbar .ds-select-wrapper { flex: 0 0 220px; }

        .rr-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1rem; }
        .rr-kpi { background: #ffffff; border: 1px solid #eceef0; border-radius: 8px; padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 0.375rem; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
        .rr-kpi__title { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: #43474e; }
        .rr-kpi__value { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 1.375rem; font-weight: 600; color: #191c1e; }
        .rr-kpi__value--pos { color: #1b7f4d; }
        .rr-kpi__value--neg { color: #ba1a1a; }

        .rr-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
        .rr-grid--2col { grid-template-columns: 1fr 1fr; }
        @media (max-width: 1080px) { .rr-grid, .rr-grid--2col { grid-template-columns: 1fr; } }

        .rr-card__head { display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
        .rr-card__head--pad { padding: 1rem 1rem 0.75rem; border-bottom: 1px solid #eceef0; margin-bottom: 0; }
        .rr-card__title { margin: 0; font-size: 1.0625rem; font-weight: 600; color: #191c1e; }
        .rr-card__meta { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; color: #74777f; }

        .rr-chart__legend { display: flex; gap: 1rem; margin-bottom: 0.75rem; }
        .rr-chart__key { display: inline-flex; align-items: center; gap: 0.375rem; font-size: 0.75rem; color: #43474e; }
        .rr-swatch { display: inline-block; width: 10px; height: 10px; border-radius: 2px; }
        .rr-swatch--forecast { background: #c4c6cf; }
        .rr-swatch--actual { background: #002045; }
        .rr-chart__rows { display: flex; flex-direction: column; gap: 0.375rem; }
        .rr-chart__row { display: grid; grid-template-columns: 56px 1fr 96px; align-items: center; gap: 0.625rem; }
        .rr-chart__month { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; color: #43474e; white-space: nowrap; }
        .rr-chart__bars { display: flex; flex-direction: column; gap: 2px; }
        .rr-chart__bar { height: 7px; border-radius: 2px; min-width: 2px; }
        .rr-chart__bar--forecast { background: #c4c6cf; }
        .rr-chart__bar--actual { background: #002045; }
        .rr-chart__val { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 10px; text-align: right; white-space: nowrap; }

        .rr-compliance { display: flex; flex-direction: column; gap: 0.75rem; }
        .rr-compliance__row { display: grid; grid-template-columns: 140px 1fr 32px; align-items: center; gap: 0.75rem; }
        .rr-compliance__label { font-size: 0.8125rem; color: #191c1e; }
        .rr-compliance__track { background: #f2f4f6; border-radius: 3px; height: 10px; overflow: hidden; }
        .rr-compliance__fill { height: 100%; border-radius: 3px; }
        .rr-compliance__fill--aligned { background: #1b7f4d; }
        .rr-compliance__fill--review { background: #b26a00; }
        .rr-compliance__fill--noncompliant { background: #ba1a1a; }
        .rr-compliance__num { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; text-align: right; font-weight: 600; }

        .rr-anoms { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
        .rr-anom { border: 1px solid #eceef0; border-radius: 6px; padding: 0.625rem 0.75rem; background: #ffffff; }
        .rr-anom__top { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
        .rr-anom__contract { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #002045; font-weight: 600; }
        .rr-anom__meta { font-size: 0.75rem; color: #43474e; flex: 1; }
        .rr-anom__message { margin: 0.375rem 0 0; font-size: 0.8125rem; color: #191c1e; }
        .rr-anom__type { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; color: #74777f; }

        .rr-scroll { overflow-x: auto; }
        .rr-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .rr-table th { position: sticky; top: 0; text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; border-bottom: 1px solid #c4c6cf; white-space: nowrap; }
        .rr-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .rr-table--nested td { background: #f7f9fb; }
        .rr-table--nested th { background: #eceef0; }

        .rr-right { text-align: right; }
        .rr-center { text-align: center; }
        .rr-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .rr-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; white-space: nowrap; }
        .rr-neg { color: #ba1a1a; }
        .rr-chev { font-size: 18px; color: #43474e; vertical-align: middle; }

        .rr-contract { cursor: pointer; }
        .rr-contract:hover td { background: #f2f4f6; }

        .rr-muted-center { margin: 0; padding: 1rem; text-align: center; color: #74777f; font-size: 0.875rem; }

        .rr-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem; padding: 3rem 2rem; color: #43474e; font-size: 0.875rem; text-align: center; }
        .rr-state--error { color: #ba1a1a; }
        .rr-state--empty { color: #74777f; }
        .rr-state__title { margin: 0; font-weight: 600; }
        .rr-state__detail { margin: 0; font-size: 0.875rem; color: #74777f; max-width: 560px; }
        .rr-state__hint { margin: 0.75rem 0 0; font-size: 0.8125rem; color: #74777f; max-width: 560px; }
      `}</style>
    </div>
  );
}